import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Flame, ShoppingBag, History, SlidersHorizontal, Sparkles, Star, User, Plus, X, LogIn, ChefHat,
  Leaf, Drumstick, Award, TrendingUp, ChevronLeft, ChevronRight, Minus, Trash2, Play, Gift, Sun, Moon, Users, Search,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";
import { dishImg, onDishImgError, DISH_PLACEHOLDER } from "../lib/dishImage";
import MenuAssistant from "../components/MenuAssistant";

const ALL_ALLERGENS = ["Gluten", "Dairy", "Nuts", "Shellfish", "Eggs", "Fish"];

const QUICK_FILTERS = [
  { key: "veg", label: "Veg", icon: Leaf, color: "#10B981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.45)", match: (i) => i.is_veg },
  { key: "nonveg", label: "Non-Veg", icon: Drumstick, color: "#EF4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.45)", match: (i) => i.is_veg === false },
  { key: "spicy", label: "Spicy", icon: Flame, color: "#F97316", bg: "rgba(249,115,22,0.12)", border: "rgba(249,115,22,0.45)", match: (i) => i.is_spicy },
  { key: "chef", label: "Chef Special", icon: Award, color: "#E5A93C", bg: "rgba(229,169,60,0.15)", border: "rgba(229,169,60,0.5)", match: (i) => i.is_chef_special },
  { key: "most", label: "Most Ordered", icon: TrendingUp, color: "#3B82F6", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.45)", match: (i) => (i.total_ordered || 0) >= 3 },
];

export default function CustomerMenu() {
  const { slug, tableNo: tableNoParam } = useParams();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { add, count, items: cartItems, total: cartTotal, inc, dec, remove, tableNo, setTableNo, appendOrderId, setAppendOrderId, setSlug, sharedActive } = useCart();
  const { theme, toggle: toggleTheme } = useTheme();

  // Tell the cart which restaurant we're on so the shared-table cart syncs
  // to the right doc. Also re-tell on slug change.
  useEffect(() => { if (slug) setSlug(slug); }, [slug, setSlug]);

  // Capture the table from the QR-scan URL (/r/:slug/t/:tableNo) so the rest
  // of the ordering flow uses the live, dynamic table number. Also, if there's
  // an in-progress unpaid order at that table, route the guest straight to it.
  useEffect(() => {
    if (!tableNoParam) return;
    setTableNo(slug, tableNoParam);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/customer/restaurant/${slug}/table/${encodeURIComponent(tableNoParam)}/active-order`);
        if (cancelled) return;
        if (data?.id && !data.paid) {
          nav(`/r/${slug}/order/${data.id}/success`, { replace: true });
        }
      } catch { /* noop — stay on the menu */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableNoParam, slug]);

  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("all");
  const [excluded, setExcluded] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = React.useRef(null);
  const [selected, setSelected] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltyNotified, setLoyaltyNotified] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/customer/restaurant/${slug}/menu`);
        setMenu(data);
      } catch { toast.error("Menu unavailable"); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get(`/customer/loyalty/${slug}`);
        setLoyalty(data);
      } catch { /* noop */ }
    })();
  }, [user, slug]);

  const filteredItems = useMemo(() => {
    if (!menu) return [];
    let items = menu.items;
    if (activeCat !== "all") items = items.filter((i) => i.category_id === activeCat);
    activeFilters.forEach((k) => {
      const f = QUICK_FILTERS.find((x) => x.key === k);
      if (f) items = items.filter(f.match);
    });
    if (excluded.length) items = items.map((i) => ({ ...i, _hidden: i.allergens?.some((a) => excluded.includes(a)) }));
    return items;
  }, [menu, activeCat, activeFilters, excluded]);

  const featured = useMemo(() => (menu?.items || []).filter((i) => i.is_featured), [menu]);
  const prevOrdered = useMemo(() => (menu?.items || []).filter((i) => i.previously_ordered), [menu]);

  // Full-text search across dish name, description, ingredients, tags and allergens.
  // Only runs when there's a query so the category view stays cheap.
  const searchResults = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q || !menu) return null;
    const tokens = q.split(/\s+/).filter(Boolean);
    const score = (it) => {
      const name = (it.name || "").toLowerCase();
      const desc = (it.description || "").toLowerCase();
      const ings = (it.ingredients || []).map((x) => x.toLowerCase());
      const tags = (it.tags || []).map((x) => x.toLowerCase());
      const allergens = (it.allergens || []).map((x) => x.toLowerCase());
      const hay = [name, desc, ings.join(" "), tags.join(" "), allergens.join(" ")].join(" ");
      // All tokens must appear somewhere; rank by how early they hit the name.
      if (!tokens.every((t) => hay.includes(t))) return -1;
      let s = 0;
      tokens.forEach((t) => {
        if (name.startsWith(t)) s += 50;
        else if (name.includes(t)) s += 25;
        if (ings.some((x) => x.includes(t))) s += 8;
        if (tags.some((x) => x.includes(t))) s += 5;
      });
      return s;
    };
    const scored = menu.items
      .map((it) => ({ it, s: score(it) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s);
    return scored.map((x) => x.it);
  }, [searchQuery, menu]);

  const toggleQuick = (k) => setActiveFilters((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const scrollToCat = (cid) => {
    setActiveCat(cid);
    // Double RAF so React commits category DOM before we measure + scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(cid === "all" ? "menu-grid-top" : `cat-section-${cid}`);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - 150; // sticky header offset
        window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
      });
    });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/50">Loading the feast…</div>;
  if (!menu) return <div className="min-h-screen flex items-center justify-center text-white/50">Menu not found</div>;

  const { restaurant, categories } = menu;

  return (
    <div className="min-h-screen pb-40" style={{ background: "var(--bg)", color: "var(--fg)" }}>
      {/* Top bar: Logo + Category tabs (sticky) */}
      <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-home">
            {restaurant.logo_url ? (
              <img src={restaurant.logo_url} alt={restaurant.name} className="w-10 h-10 rounded-full object-cover border" style={{ borderColor: "var(--accent)" }} data-testid="restaurant-logo" />
            ) : (
              <div className="w-8 h-8 rounded-full text-black flex items-center justify-center" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}><Flame className="w-4 h-4" /></div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: "var(--fg-muted)" }}>{restaurant.cuisine}</div>
              <div className="font-display text-lg leading-none">{restaurant.name}</div>
              {restaurant.tagline && <div className="text-[10px] italic mt-0.5" style={{ color: "var(--fg-muted)" }}>{restaurant.tagline}</div>}
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} data-testid="theme-toggle" title="Toggle theme" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/5 transition-colors" style={{ background: "var(--glass-light)", border: "1px solid var(--border)" }}>
              {theme === "dark" ? <Sun className="w-4 h-4" style={{ color: "var(--accent)" }} /> : <Moon className="w-4 h-4" style={{ color: "var(--accent)" }} />}
            </button>
            <button
              onClick={() => {
                const willOpen = !searchOpen;
                setSearchOpen(willOpen);
                if (!willOpen) setSearchQuery("");
                if (willOpen) setTimeout(() => searchInputRef.current?.focus(), 50);
              }}
              data-testid="search-btn"
              title="Search dishes"
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/5 transition-colors relative"
              style={{ background: "var(--glass-light)", border: "1px solid var(--border)" }}
            >
              <Search className="w-4 h-4" style={{ color: searchQuery ? "var(--accent)" : "var(--fg)" }} />
              {searchQuery && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ background: "var(--accent)" }} />
              )}
            </button>
            <button onClick={() => setFilterOpen(true)} data-testid="filters-btn" className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors" style={{ background: "var(--glass-light)", border: "1px solid var(--border)" }}>
              <SlidersHorizontal className="w-3.5 h-3.5" style={{ color: "var(--accent)" }} />
              <span className="text-xs font-medium">Filters</span>
              {(activeFilters.length + excluded.length) > 0 && (
                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>{activeFilters.length + excluded.length}</span>
              )}
            </button>
            {user ? (
              <Link to={`/r/${slug}/vault`} data-testid="vault-link" className="p-2 rounded-full hover:bg-white/5" title="Order Vault"><History className="w-5 h-5" /></Link>
            ) : (
              <Link to="/login/customer" state={{ from: `/r/${slug}` }} data-testid="menu-login-link" className="p-2 rounded-full hover:bg-white/5"><LogIn className="w-5 h-5" /></Link>
            )}
            {user && (
              <button onClick={() => { logout(); toast.success("Signed out"); }} data-testid="customer-logout" className="text-xs px-3 py-1.5 rounded-full hover:bg-white/5" style={{ border: "1px solid var(--border)" }}>
                <User className="w-3.5 h-3.5 inline mr-1" /> {user.name.split(" ")[0]}
              </button>
            )}
          </div>
        </div>

        {/* Expanding search bar — appears only when search is toggled on */}
        {searchOpen && (
          <div className="max-w-5xl mx-auto px-4 pb-3" data-testid="search-bar">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--fg-muted)" }} />
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") { setSearchQuery(""); setSearchOpen(false); } }}
                placeholder='Search dishes — try "truffle", "vegan", "spicy chicken"…'
                data-testid="search-input"
                className="w-full pl-10 pr-10 py-2.5 rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-[var(--accent)] transition"
                style={{ background: "var(--bg-card)", border: "1px solid var(--border)", color: "var(--fg)" }}
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(""); searchInputRef.current?.focus(); }}
                  data-testid="search-clear"
                  title="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center hover:opacity-80"
                  style={{ background: "var(--glass-light)" }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Category tabs — sticky under logo (hidden while searching so the bar stays clean) */}
        {!searchQuery && (
          <div className="max-w-5xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto" data-testid="category-tabs">
            <CatTab active={activeCat === "all"} onClick={() => scrollToCat("all")} label="All" testid="cat-all" />
            {categories.map((c) => (
              <CatTab key={c.id} active={activeCat === c.id} onClick={() => scrollToCat(c.id)} label={c.name} testid={`cat-${c.id}`} />
            ))}
          </div>
        )}
      </header>

      {/* Appending-to-order banner — guest tapped "Add more dishes" on OrderSuccess */}
      {appendOrderId && (
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <div className="p-3 rounded-2xl flex items-center gap-3" style={{ border: "1px dashed var(--accent)", background: "rgba(229,169,60,0.08)" }} data-testid="append-banner">
            <ChefHat className="w-4 h-4" style={{ color: "var(--accent)" }} />
            <div className="flex-1 text-sm">
              <span className="font-display">Adding to your running order</span>
              <span className="text-xs ml-2" style={{ color: "var(--fg-muted)" }}>{tableNo ? `· Table ${tableNo}` : ""}</span>
            </div>
            <button onClick={() => { setAppendOrderId(slug, null); toast("Reverted to a new order"); }} data-testid="cancel-append" className="text-[10px] uppercase tracking-widest font-mono px-2 py-1 rounded-full hover:bg-white/5" style={{ border: "1px solid var(--border)" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Loyalty banner */}
      {loyalty?.eligible && !loyaltyNotified && (
        <motion.div initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-w-5xl mx-auto px-4 pt-4">
          <div className="p-4 rounded-2xl flex items-start gap-3" style={{ border: "1px solid var(--accent)", background: "linear-gradient(to right, rgba(229,169,60,0.15), transparent)" }} data-testid="loyalty-banner">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}><Gift className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>Regular perk unlocked, {user?.name?.split(" ")[0]}</div>
              <div className="font-display text-lg mt-0.5">{loyalty.message}</div>
              {loyalty.complimentary_item && <div className="text-xs mt-1" style={{ color: "var(--fg-muted)" }}>Complimentary: {loyalty.complimentary_item.name}</div>}
            </div>
            <button onClick={() => setLoyaltyNotified(true)} className="opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
          </div>
        </motion.div>
      )}

      {/* Search results — takes over the main area while a query is active */}
      {searchResults !== null && (
        <section className="px-4 mt-6 max-w-5xl mx-auto" data-testid="search-results">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl">
              {searchResults.length > 0 ? "Search results" : "No dishes match"}
              <span className="ml-2 text-[11px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>
                {searchResults.length} found for "{searchQuery}"
              </span>
            </h2>
            <button
              onClick={() => { setSearchQuery(""); setSearchOpen(false); }}
              data-testid="search-exit"
              className="text-[11px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 rounded-full hover:bg-white/5"
              style={{ border: "1px solid var(--border)", color: "var(--fg-muted)" }}
            >
              Clear
            </button>
          </div>
          {searchResults.length === 0 ? (
            <div className="p-8 rounded-2xl text-center" style={{ border: "1px dashed var(--border)", color: "var(--fg-muted)" }}>
              <Search className="w-5 h-5 mx-auto mb-2" style={{ color: "var(--accent)" }} />
              <div>Nothing on today's menu matches "{searchQuery}".</div>
              <div className="text-xs mt-2" style={{ color: "var(--fg-dim)" }}>
                Try a broader word, or tap the AI Sommelier below — it can suggest the closest match.
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="search-results-grid">
              {searchResults.map((i) => (
                <ItemCard key={i.id} item={i} onAdd={() => { add(i, restaurant.id); toast.success(`${i.name} added`); }} onOpen={() => setSelected(i)} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Welcome Back + Previously ordered */}
      {searchResults === null && user && prevOrdered.length > 0 && (
        <section className="px-4 pt-6 max-w-5xl mx-auto">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>Welcome back, {user.name.split(" ")[0]}</div>
              <h2 className="font-display text-3xl mt-1">Your frequent favorites</h2>
            </div>
            <div className="text-xs" style={{ color: "var(--fg-muted)" }}>Visit #{user.visit_count || 1}</div>
          </div>
          <HorizontalRow items={prevOrdered} onOpen={setSelected} onAdd={(i) => { add(i, restaurant.id); toast.success(`${i.name} added`); }} testidPrefix="prev" />
        </section>
      )}

      {/* Tonight's cravings — HORIZONTAL SLIDER */}
      {searchResults === null && featured.length > 0 && (
        <section className="pt-10 max-w-5xl mx-auto">
          <div className="flex items-baseline justify-between px-4">
            <h2 className="font-display text-3xl">Tonight's cravings</h2>
            <span className="text-[11px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>{featured.length} featured</span>
          </div>
          <FeaturedSlider items={featured} onOpen={setSelected} onAdd={(i) => { add(i, restaurant.id); toast.success(`${i.name} added`); }} />
        </section>
      )}

      {/* Quick color-coded filter chips — moved below featured */}
      {searchResults === null && (
      <section className="max-w-5xl mx-auto px-4 mt-10">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-display text-xl">Quick filters</h3>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>tap to combine</span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {QUICK_FILTERS.map((f) => {
            const Ico = f.icon;
            const active = activeFilters.includes(f.key);
            return (
              <button key={f.key} onClick={() => toggleQuick(f.key)} data-testid={`qf-${f.key}`}
                style={{
                  background: active ? f.color : f.bg,
                  color: active ? "#fff" : f.color,
                  borderColor: f.border,
                }}
                className="whitespace-nowrap flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold border transition-all hover:scale-105">
                <Ico className="w-4 h-4" style={{ color: active ? "#fff" : f.color }} /> {f.label}
              </button>
            );
          })}
        </div>
      </section>
      )}

      {searchResults === null && (
      <section className="px-4 mt-8 max-w-5xl mx-auto" id="menu-grid-top">
        {activeCat === "all" ? (
          // Render each category as its own section
          categories.map((c) => {
            const catItems = filteredItems.filter((i) => i.category_id === c.id);
            if (catItems.length === 0) return null;
            return (
              <div key={c.id} id={`cat-section-${c.id}`} className="mb-10 scroll-mt-[170px]">
                <h3 className="font-display text-2xl mb-4 flex items-baseline gap-3">
                  {c.name}
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>{catItems.length} dishes</span>
                </h3>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {catItems.map((i) => (
                    <ItemCard key={i.id} item={i} hidden={i._hidden} onAdd={() => { add(i, restaurant.id); toast.success(`${i.name} added`); }} onOpen={() => setSelected(i)} />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div id={`cat-section-${activeCat}`} className="scroll-mt-[170px]">
            <h3 className="font-display text-2xl mb-4">
              {categories.find((c) => c.id === activeCat)?.name}
            </h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {filteredItems.map((i) => (
                <ItemCard key={i.id} item={i} hidden={i._hidden} onAdd={() => { add(i, restaurant.id); toast.success(`${i.name} added`); }} onOpen={() => setSelected(i)} />
              ))}
              {filteredItems.length === 0 && (
                <div className="col-span-full text-center py-12" style={{ color: "var(--fg-dim)" }}>No dishes match your filters.</div>
              )}
            </div>
          </div>
        )}
      </section>
      )}

      {/* Bottom spacer — ensures last category can scroll up to land just below sticky header on short menus */}
      <div aria-hidden style={{ minHeight: "60vh" }} />

      {/* Sticky cart pill — expands to drawer */}
      {count > 0 && (
        <>
          <motion.button
            initial={{ y: 80 }} animate={{ y: 0 }}
            onClick={() => setCartOpen(true)} data-testid="sticky-cart"
            className="fixed bottom-5 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[420px] z-40 glass rounded-2xl border border-[#E5A93C]/40 p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#E5A93C] text-black flex items-center justify-center font-bold">{count}</div>
              <div className="text-left">
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>
                  {sharedActive ? `Shared tray · Table ${tableNo}` : "Your tray"}
                </div>
                <div className="text-sm font-semibold">View cart · ₹{cartTotal.toFixed(0)}</div>
              </div>
            </div>
            <span className="gold-btn rounded-full px-4 py-2 text-xs font-semibold">Open →</span>
          </motion.button>
        </>
      )}

      <AnimatePresence>
        {cartOpen && (
          <CartDrawer items={cartItems} total={cartTotal} onClose={() => setCartOpen(false)}
            inc={inc} dec={dec} remove={remove} sharedActive={sharedActive} tableNo={tableNo}
            onCheckout={() => { setCartOpen(false); nav(`/r/${slug}/cart`); }} loyalty={loyalty} />
        )}
        {selected && <ItemDetail item={selected} allItems={menu.items} onClose={() => setSelected(null)} onAdd={(it) => { add(it, restaurant.id); toast.success(`${it.name} added`); }} user={user} />}
        {filterOpen && <AllergenPanel excluded={excluded} setExcluded={setExcluded} activeFilters={activeFilters} toggleQuick={toggleQuick} onClose={() => setFilterOpen(false)} />}
      </AnimatePresence>

      <MenuAssistant slug={slug} restaurant={restaurant} onAddItem={(it) => add(it, restaurant.id)} />

      <ShoppingBag className="hidden" />
    </div>
  );
}

/* ---------- Featured slider ---------- */
function FeaturedSlider({ items, onOpen, onAdd }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (!ref.current) return;
    const w = ref.current.clientWidth;
    ref.current.scrollBy({ left: dir * (w * 0.7), behavior: "smooth" });
  };
  return (
    <div className="relative mt-5 group">
      <button onClick={() => scroll(-1)} data-testid="slider-prev" className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full glass items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button onClick={() => scroll(1)} data-testid="slider-next" className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full glass items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <ChevronRight className="w-5 h-5" />
      </button>
      <div ref={ref} className="flex gap-4 overflow-x-auto scroll-smooth px-4 snap-x snap-mandatory pb-2" style={{ scrollSnapType: "x mandatory" }} data-testid="featured-slider">
        {items.map((i) => (
          <div key={i.id} className="snap-start shrink-0 w-[82%] sm:w-[50%] md:w-[42%]">
            <HeroCard item={i} onOpen={() => onOpen(i)} onAdd={() => onAdd(i)} />
          </div>
        ))}
      </div>
    </div>
  );
}

function HorizontalRow({ items, onOpen, onAdd, testidPrefix }) {
  return (
    <div className="mt-4 flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
      {items.map((i) => (
        <div key={i.id} className="min-w-[220px] relative rounded-2xl overflow-hidden border border-white/10 group cursor-pointer" onClick={() => onOpen(i)} data-testid={`${testidPrefix}-${i.id}`}>
          <img src={dishImg(i)} onError={onDishImgError} alt={i.name} className="h-40 w-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
          <div className="absolute top-2 left-2 flex gap-1 on-photo">
            <VegDot veg={i.is_veg} />
            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full glass-light">You loved this</span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-3 on-photo">
            <div className="font-display text-lg leading-tight">{i.name}</div>
            {i.rating_count ? (
              <div className="flex items-center gap-1 text-[11px] text-white/90">
                {[1, 2, 3, 4, 5].map((n) => (<Star key={n} className={`w-3 h-3 ${n <= Math.round(i.avg_rating || 0) ? "fill-[#E5A93C] text-[#E5A93C]" : "text-white/30"}`} />))}
                <span className="font-mono">{i.avg_rating?.toFixed(1)} ({i.rating_count})</span>
              </div>
            ) : null}
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-[#E5A93C]">₹{i.price}</span>
              <button onClick={(e) => { e.stopPropagation(); onAdd(i); }} className="w-7 h-7 rounded-full bg-[#E5A93C] text-black flex items-center justify-center">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CatTab({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      style={{
        background: active ? "var(--accent)" : "transparent",
        color: active ? "var(--accent-fg)" : "var(--fg)",
        borderColor: active ? "var(--accent)" : "var(--border)",
      }}
      className="whitespace-nowrap px-4 py-2 rounded-full text-sm transition-colors border hover:opacity-90 font-semibold">
      {label}
    </button>
  );
}

function VegDot({ veg }) {
  return (
    <span title={veg ? "Vegetarian" : "Non-Veg"} className={`w-4 h-4 rounded-sm border flex items-center justify-center ${veg ? "border-emerald-500" : "border-red-500"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${veg ? "bg-emerald-500" : "bg-red-500"}`} />
    </span>
  );
}

function RatingBar({ avg, count }) {
  if (!count) return null;
  return (
    <div className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color: "var(--fg-muted)" }} data-testid="rating-bar">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className="w-3 h-3" style={{ color: n <= Math.round(avg) ? "var(--accent)" : "var(--fg-dim)", fill: n <= Math.round(avg) ? "var(--accent)" : "transparent" }} />
      ))}
      <span className="font-mono">{avg?.toFixed(1)}</span>
      <span style={{ color: "var(--fg-dim)" }}>({count})</span>
    </div>
  );
}

function HeroCard({ item, onOpen, onAdd }) {
  return (
    <motion.div whileHover={{ y: -3 }} className="relative rounded-3xl overflow-hidden border border-white/10 cursor-pointer group h-full" onClick={onOpen} data-testid={`hero-item-${item.id}`}>
      <div className="aspect-[4/5] relative">
        <img src={item.image_url} alt={item.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
        <div className="absolute top-4 left-4 right-4 flex gap-2 flex-wrap items-start on-photo">
          <VegDot veg={item.is_veg} />
          {item.is_spicy && <span className="text-[10px] px-2 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 font-mono uppercase tracking-widest flex items-center gap-1"><Flame className="w-3 h-3" /> Spicy</span>}
          {item.is_chef_special && <span className="text-[10px] px-2 py-1 rounded-full bg-[#E5A93C]/90 font-mono uppercase tracking-widest flex items-center gap-1" style={{ color: "#0A0A0C" }}><Award className="w-3 h-3" /> Chef Special</span>}
          {item.is_sold_out && <span className="text-[10px] px-2 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 font-mono uppercase tracking-widest">Sold out</span>}
          {item.social_order_count != null && item.social_order_count > 0 && (
            <span className="ml-auto text-[10px] px-2 py-1 rounded-full glass-light font-mono uppercase tracking-widest">Ordered {item.social_order_count.toLocaleString()}×</span>
          )}
        </div>
        {item.previously_ordered && (
          <div className="absolute top-12 left-4 text-[10px] px-2 py-1 rounded-full bg-[#E5A93C]/90 font-mono uppercase tracking-widest" style={{ color: "#0A0A0C" }}>Previously ordered</div>
        )}
        {item.videos?.length > 0 && (
          <div className="absolute top-4 right-4 w-9 h-9 rounded-full glass-light flex items-center justify-center on-photo"><Play className="w-4 h-4" /></div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-5 on-photo">
          <div className="flex items-center gap-1 text-xs mb-1">
            <Sparkles className="w-3.5 h-3.5" style={{ color: "#E5A93C" }} />
            <span className="font-mono uppercase tracking-widest">Featured</span>
          </div>
          <h3 className="font-display text-3xl leading-tight">{item.name}</h3>
          <p className="text-sm mt-1 line-clamp-2">{item.description}</p>
          {item.rating_count ? (
            <div className="flex items-center gap-1 text-[11px] text-white/90 mt-0.5">
              {[1, 2, 3, 4, 5].map((n) => (<Star key={n} className={`w-3 h-3 ${n <= Math.round(item.avg_rating || 0) ? "fill-[#E5A93C] text-[#E5A93C]" : "text-white/30"}`} />))}
              <span className="font-mono">{item.avg_rating?.toFixed(1)}</span>
              <span className="text-white/60">({item.rating_count})</span>
            </div>
          ) : null}
          <div className="mt-4 flex items-center justify-between">
            <div className="font-display text-2xl text-[#E5A93C]">₹{item.price}</div>
            {!item.is_sold_out && (
              <button onClick={(e) => { e.stopPropagation(); onAdd(); }} data-testid={`hero-add-${item.id}`} className="gold-btn rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ItemCard({ item, hidden, onAdd, onOpen }) {
  return (
    <motion.div whileHover={{ y: -3 }} className={`relative rounded-2xl overflow-hidden cursor-pointer ${hidden ? "opacity-30 grayscale" : ""}`} style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }} onClick={onOpen} data-testid={`item-${item.id}`}>
      <div className="relative aspect-[5/4]">
        <img src={dishImg(item)} onError={onDishImgError} alt={item.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute top-2 left-2 flex gap-1">
          <VegDot veg={item.is_veg} />
          {item.is_spicy && <Flame className="w-3.5 h-3.5 text-orange-500" />}
          {item.is_chef_special && <Award className="w-3.5 h-3.5" style={{ color: "var(--accent)" }} />}
        </div>
        {item.is_sold_out && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-sm font-mono uppercase tracking-widest text-red-300">Sold Out</span>
          </div>
        )}
        {item.previously_ordered && (
          <span className="absolute top-2 right-2 text-[9px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>Previous</span>
        )}
        {item.videos?.length > 0 && <div className="absolute bottom-2 right-2 w-7 h-7 rounded-full glass-light flex items-center justify-center"><Play className="w-3 h-3" /></div>}
      </div>
      <div className="p-3">
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="font-display text-lg leading-tight">{item.name}</h4>
          <div className="text-sm font-mono" style={{ color: "var(--accent)" }}>₹{item.price}</div>
        </div>
        {item.calories != null && item.calories > 0 && (
          <div className="text-[10px] mt-0.5 font-mono" style={{ color: "var(--fg-muted)" }} data-testid={`kcal-${item.id}`}>
            {item.calories} kcal
          </div>
        )}
        <RatingBar avg={item.avg_rating} count={item.rating_count} />
        <p className="text-xs line-clamp-2 mt-1" style={{ color: "var(--fg-muted)" }}>{item.description}</p>
        <div className="flex items-center justify-between mt-3">
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>
            {item.social_order_count != null && item.social_order_count > 0
              ? `Ordered ${item.social_order_count.toLocaleString()}×`
              : ""}
          </div>
          {!item.is_sold_out && (
            <button onClick={(e) => { e.stopPropagation(); onAdd(); }} data-testid={`add-${item.id}`} className="rounded-full px-3 py-1.5 text-xs gold-btn font-semibold flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ---------- Item detail with multi-media gallery ---------- */
function ItemDetail({ item, allItems, onClose, onAdd, user }) {
  const pairings = (item.pairing_item_ids || []).map((id) => allItems.find((x) => x.id === id)).filter(Boolean);
  const gallery = useMemo(() => {
    const imgs = (item.images && item.images.length ? item.images : [item.image_url]).filter((u) => typeof u === "string" && u.trim().length > 0);
    const vids = (item.videos || []).filter(Boolean);
    const list = [...vids.map((u) => ({ type: "video", url: u })), ...imgs.map((u) => ({ type: "image", url: u }))];
    // If owner has uploaded nothing, surface the branded placeholder so guests see it
    if (list.length === 0) list.push({ type: "image", url: DISH_PLACEHOLDER });
    return list;
  }, [item]);
  const [idx, setIdx] = useState(0);
  const current = gallery[idx] || { type: "image", url: DISH_PLACEHOLDER };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-end justify-center bg-black/70" onClick={onClose} data-testid="item-detail">
      <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 100 }} transition={{ type: "spring", damping: 28 }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-[#0F1115] rounded-t-3xl md:rounded-3xl md:mb-10 border-t md:border border-white/10 overflow-hidden max-h-[88vh] overflow-y-auto">
        <div className="relative aspect-[16/10] bg-black">
          {current.type === "video" ? (
            <video src={current.url} className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline />
          ) : (
            <img src={current.url} onError={onDishImgError} alt={item.name} className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full glass-light flex items-center justify-center" data-testid="close-detail"><X className="w-4 h-4" /></button>
          {gallery.length > 1 && (
            <>
              <button onClick={() => setIdx((i) => (i - 1 + gallery.length) % gallery.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full glass-light flex items-center justify-center" data-testid="gallery-prev"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setIdx((i) => (i + 1) % gallery.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full glass-light flex items-center justify-center" data-testid="gallery-next"><ChevronRight className="w-4 h-4" /></button>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1">
                {gallery.map((g, i) => <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition ${i === idx ? "bg-[#E5A93C] w-5" : "bg-white/40"}`} />)}
              </div>
            </>
          )}
          <div className="absolute bottom-4 left-5 right-5">
            <div className="flex items-center gap-2">
              <VegDot veg={item.is_veg} />
              {item.is_spicy && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 font-mono uppercase tracking-widest">Spicy</span>}
              {item.is_chef_special && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E5A93C]/90 text-black font-mono uppercase tracking-widest">Chef Special</span>}
            </div>
            <h3 className="font-display text-4xl mt-1">{item.name}</h3>
            <RatingBar avg={item.avg_rating} count={item.rating_count} />
          </div>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-white/70 leading-relaxed">{item.description}</p>
          {item.allergens?.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Contains</div>
              <div className="mt-1 flex gap-1 flex-wrap">
                {item.allergens.map((a) => <span key={a} className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10">{a}</span>)}
              </div>
            </div>
          )}
          {pairings.length > 0 && (
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center gap-2 text-[#E5A93C]">
                <Sparkles className="w-4 h-4" />
                <div className="text-xs font-mono uppercase tracking-widest">Digital Sommelier</div>
              </div>
              <p className="text-xs text-white/50 mt-1">Perfect pairings for this dish</p>
              <div className="mt-3 flex gap-3 overflow-x-auto">
                {pairings.map((p) => (
                  <div key={p.id} className="min-w-[160px] rounded-2xl overflow-hidden border border-white/10 relative cursor-pointer" onClick={() => onAdd(p)} data-testid={`pairing-${p.id}`}>
                    <img src={p.image_url} alt={p.name} className="h-32 w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-2">
                      <div className="font-display text-sm">{p.name}</div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs gold-text">₹{p.price}</span>
                        <span className="text-[10px] text-white/60">+ pair</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between pt-2">
            <div>
              <div className="font-display text-3xl gold-text">₹{item.price}</div>
              {item.calories != null && item.calories > 0 && (
                <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--fg-muted)" }} data-testid="detail-kcal">
                  {item.calories} kcal
                </div>
              )}
            </div>
            {!item.is_sold_out ? (
              <button onClick={() => { onAdd(item); onClose(); }} data-testid="detail-add-btn" className="gold-btn rounded-full px-6 py-3 font-semibold">Add to table</button>
            ) : (
              <span className="text-red-400 text-sm">Currently sold out</span>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ---------- Allergen + quick-filter panel ---------- */
function AllergenPanel({ excluded, setExcluded, activeFilters, toggleQuick, onClose }) {
  const toggle = (a) => setExcluded((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center" onClick={onClose} data-testid="filters-panel">
      <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0F1115] rounded-t-3xl md:rounded-3xl border border-white/10 p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl">Filter menu</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-light flex items-center justify-center" data-testid="close-filters"><X className="w-4 h-4" /></button>
        </div>

        <div className="mt-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Preferences</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {QUICK_FILTERS.map((f) => {
              const Ico = f.icon; const active = activeFilters.includes(f.key);
              return (
                <button key={f.key} onClick={() => toggleQuick(f.key)} data-testid={`panel-qf-${f.key}`} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm border transition-colors ${active ? "bg-[#E5A93C] text-black border-[#E5A93C] font-semibold" : "border-white/10 hover:bg-white/5"}`}>
                  <Ico className="w-4 h-4" /> {f.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Hide dishes containing</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {ALL_ALLERGENS.map((a) => (
              <button key={a} onClick={() => toggle(a)} data-testid={`allergen-${a}`} className={`px-4 py-2.5 rounded-xl border text-sm transition-colors ${excluded.includes(a) ? "bg-[#E5A93C] text-black border-[#E5A93C]" : "border-white/10 hover:bg-white/5"}`}>
                {a}
              </button>
            ))}
          </div>
        </div>
        <button onClick={onClose} className="mt-6 w-full gold-btn rounded-full py-3 font-semibold" data-testid="apply-filters">Apply</button>
      </motion.div>
    </motion.div>
  );
}

/* ---------- Cart drawer ---------- */
function CartDrawer({ items, total, onClose, inc, dec, remove, onCheckout, loyalty, sharedActive, tableNo }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center" onClick={onClose} data-testid="cart-drawer">
      <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0F1115] rounded-t-3xl md:rounded-3xl border border-white/10 p-5 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
              {sharedActive ? `Shared tray · Table ${tableNo}` : "Your tray"}
            </div>
            <h3 className="font-display text-2xl">{items.length} items</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-light flex items-center justify-center" data-testid="close-cart"><X className="w-4 h-4" /></button>
        </div>

        {sharedActive && (
          <div className="mt-3 p-3 rounded-xl border border-[#E5A93C]/25 flex items-start gap-2 text-xs" style={{ background: "var(--glass-light)", color: "var(--fg-muted)" }} data-testid="shared-cart-banner">
            <Users className="w-3.5 h-3.5 mt-0.5" style={{ color: "var(--accent)" }} />
            <div>
              <span className="font-semibold" style={{ color: "var(--accent)" }}>Shared with your table.</span> Anything you or your friends add here lives on one bill. Updates sync every few seconds.
            </div>
          </div>
        )}

        {loyalty?.eligible && (
          <div className="mt-4 p-3 rounded-xl border border-[#E5A93C]/30 bg-[#E5A93C]/10 text-sm flex items-start gap-2" data-testid="loyalty-preview">
            <Gift className="w-4 h-4 text-[#E5A93C] mt-0.5" />
            <div>
              <div className="font-semibold text-[#E5A93C]">Loyalty perks applied at checkout</div>
              <div className="text-xs text-white/70 mt-0.5">
                {loyalty.discount_pct > 0 && <>• {loyalty.discount_pct}% off your bill<br /></>}
                {loyalty.complimentary_item && <>• Complimentary {loyalty.complimentary_item.name}</>}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-2">
          {items.map((it) => (
            <div key={it.item_id} className="flex gap-3 p-2 rounded-xl border border-white/5">
              <img src={dishImg(it)} onError={onDishImgError} alt={it.name} className="w-14 h-14 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-base truncate">{it.name}</div>
                <div className="text-xs text-white/50 font-mono">₹{it.price}</div>
                <div className="mt-1 flex items-center gap-2">
                  <button onClick={() => dec(it.item_id)} className="w-6 h-6 rounded-full border border-white/15 hover:bg-white/5 flex items-center justify-center" data-testid={`drawer-dec-${it.item_id}`}><Minus className="w-3 h-3" /></button>
                  <span className="w-5 text-center text-sm">{it.qty}</span>
                  <button onClick={() => inc(it.item_id)} className="w-6 h-6 rounded-full border border-white/15 hover:bg-white/5 flex items-center justify-center" data-testid={`drawer-inc-${it.item_id}`}><Plus className="w-3 h-3" /></button>
                  <button onClick={() => remove(it.item_id)} className="ml-auto text-xs text-white/40 hover:text-red-400" data-testid={`drawer-rm-${it.item_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="font-mono gold-text self-start text-sm">₹{(it.price * it.qty).toFixed(0)}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 border-t border-white/10 pt-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-white/50">Subtotal</div>
            <div className="font-display text-2xl gold-text">₹{total.toFixed(0)}</div>
          </div>
          <button onClick={onCheckout} data-testid="drawer-checkout" className="gold-btn rounded-full px-6 py-3 font-semibold">Review & send</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
